import React, { useState, useEffect, useRef } from 'react';
import { useGeneral } from '../../../contexts';
import Button from '../Button/Button';
import Services from '../../../services/Services';
import styles from './PrivateChannelError.module.css';

interface PrivateChannelErrorProps {
  channelID: string;
  channelName?: string;
  onJoinClick?: () => void;
}

const PrivateChannelError: React.FC<PrivateChannelErrorProps> = ({
  channelID,
  channelName,
  onJoinClick
}) => {
  const { auth, language, toastError, toastSuccess } = useGeneral();
  const { token, user } = auth;
  const { getTranslation } = language;
  const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);
  const [isPending, setIsPending] = useState<boolean>(false);
  const [loadingPending, setLoadingPending] = useState<boolean>(true);
  const isMountedRef = useRef(true);

  const handleJoinClick = () => {
    if (!token || !user?.id) {
      toastError(getTranslation('home.loginRequired') || 'Please login to join communities');
      return;
    }
    // Don't show dialog if already pending
    if (isPending) {
      return;
    }
    setShowConfirmDialog(true);
  };

  const handleCancel = () => {
    setShowConfirmDialog(false);
  };

  // Check if user has a pending join request
  useEffect(() => {
    isMountedRef.current = true;
    
    const checkPendingStatus = async () => {
      if (!token || !user?.id || !channelID) {
        if (isMountedRef.current) {
          setLoadingPending(false);
        }
        return;
      }

      try {
        const res = await Services.SocialHiveGetChannelUserRequests(token, channelID);
        
        if (isMountedRef.current && res.data.status && Array.isArray(res.data.result)) {
          const pendingUsers = res.data.result;
          const userIsPending = pendingUsers.some((pendingUser: any) => 
            String(pendingUser.id || pendingUser.userID) === String(user.id)
          );
          setIsPending(userIsPending);
        }
      } catch (error: any) {
        // Ignore abort errors
        if (error.name !== 'AbortError' && error.name !== 'CanceledError' && error.code !== 'ECONNABORTED') {
          // Error checking pending status — fall through to default behavior
        }
      } finally {
        if (isMountedRef.current) {
          setLoadingPending(false);
        }
      }
    };

    checkPendingStatus();

    return () => {
      isMountedRef.current = false;
    };
  }, [token, user?.id, channelID]);

  const handleConfirm = async () => {
    setShowConfirmDialog(false);
    if (!token || !user?.id || !channelID) {
      toastError(getTranslation('communities.joinError'));
      return;
    }

    try {
      const response = await Services.SocialHiveJoinToChannel(token, user.id, channelID);
      if (response.data.status) {
        // Show toast for join request sent
        toastSuccess(getTranslation('communities.joinRequestSent') || getTranslation('communities.joinRequestSuccess') || 'Join request sent');
        // Update pending status after successful request
        setIsPending(true);
      } else {
        const errorMessage = response.data?.message || getTranslation('communities.joinError') || 'Failed to join the community';
        toastError(errorMessage);
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 
                          error.message || 
                          getTranslation('communities.joinError') || 
                          'Failed to join the community';
      toastError(errorMessage);
    }
  };

  return (
    <div className={styles.privateChannelError}>
      <div className={styles.errorContent}>
        <h2 className={styles.errorTitle}>
          {getTranslation('communities.privateChannelError') || 'You need to join this community first to see their events'}
        </h2>
        <Button
          variant="primary"
          size="large"
          onClick={handleJoinClick}
          className={styles.joinButton}
          disabled={isPending || loadingPending}
        >
          {loadingPending
            ? (getTranslation('common.loading') || 'Loading...')
            : isPending
            ? (getTranslation('communities.requestSent') || getTranslation('communities.joinRequestSent') || 'Request Sent')
            : (getTranslation('communities.joinCommunity') || 'Join Community')
          }
        </Button>
      </div>

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className={styles.modalOverlay} onClick={handleCancel}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3 className={styles.modalTitle}>
              {getTranslation('communities.confirmJoin')}
            </h3>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <Button
                variant="secondary"
                size="large"
                onClick={handleCancel}
                className={styles.modalButton}
              >
                {getTranslation('common.cancel')}
              </Button>
              <Button
                variant="primary"
                size="large"
                onClick={handleConfirm}
                className={styles.modalButton}
              >
                {getTranslation('communities.joinCommunity')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrivateChannelError;

